<app-menu>
  <div class="pb-shell min-h-full" [attr.data-theme]="theme">
    <div class="mx-auto flex min-w-0 max-w-[1900px] flex-col gap-3 px-3 py-3 sm:px-5 pb-layout">
      <header
        class="pb-panel flex flex-wrap items-center justify-between gap-4 rounded-2xl px-5 py-4"
      >
        <div>
          <p class="pb-eyebrow">Warehouse / Products</p>
          <h1 class="text-3xl font-bold tracking-tight">Products</h1>
          <p class="pb-muted mt-1 text-sm">
            Find products across customers, shipments and warehouse spots.
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <a class="pb-button" routerLink="/return-batches"><i class="fa-solid fa-rotate-left" aria-hidden="true"></i> Return Batches</a>
          <div class="mr-3 text-right" aria-live="polite">
            <strong class="block text-2xl font-bold">
              {{ recordCount === null ? '—' : (recordCount | number) }}
            </strong>
            <span class="pb-muted text-xs">matching records</span>
          </div>
          <button
            class="pb-button"
            type="button"
            (click)="toggleTheme()"
            [attr.aria-label]="theme === 'dark' ? 'Use light theme' : 'Use dark theme'"
          >
            <i
              class="fa-solid"
              [class.fa-sun]="theme === 'dark'"
              [class.fa-moon]="theme === 'light'"
            ></i>
            {{ theme === 'dark' ? 'Light' : 'Dark' }}
          </button>
          <button class="pb-button" type="button" (click)="reset()">Reset view</button>
          <button class="pb-button" type="button" (click)="refresh()">
            <i class="fa-solid fa-rotate" [class.fa-spin]="loading"></i>
            Refresh
          </button>
          <span [title]="exportHint">
            <button
              class="pb-button pb-primary"
              type="button"
              [disabled]="!canExport"
              (click)="exportCsv()"
            >
              <i class="fa-solid fa-download"></i>
              {{ exporting ? 'Exporting…' : 'Export CSV' }}
            </button>
          </span>
        </div>
      </header>
      <section class="pb-panel rounded-2xl p-4" aria-label="Product filters">
        <div
          class="grid gap-3 lg:grid-cols-[minmax(260px,1.5fr)_minmax(220px,1fr)_minmax(220px,1fr)_auto] lg:items-end"
        >
          <label class="pb-label">
            Search
            <input
              class="pb-input"
              type="search"
              placeholder="Model, serial, barcode, description or reference"
              [(ngModel)]="filters.q"
              (ngModelChange)="searchChanged()"
              maxlength="150"
            />
          </label>
          <label class="pb-label">
            Customer
            <select
              class="pb-input"
              [(ngModel)]="filters.customer"
              (ngModelChange)="refresh()"
              [disabled]="optionsLoading"
            >
              <option value="">All customers</option>
              <option *ngFor="let c of options.customers" [value]="c.customer_key">
                {{ c.customer_name }} ({{ c.record_count | number }})
              </option>
            </select>
          </label>
          <label class="pb-label">
            Warehouse
            <select
              class="pb-input"
              [(ngModel)]="filters.warehouse"
              (ngModelChange)="refresh()"
              [disabled]="optionsLoading"
            >
              <option value="">All warehouses / no recorded spot</option>
              <option *ngFor="let w of options.warehouses" [value]="w.warehouse_uuid">
                {{ w.warehouse_name }}
              </option>
            </select>
          </label>
          <button
            class="pb-button h-[42px]"
            type="button"
            (click)="advanced = !advanced"
            [attr.aria-expanded]="advanced"
          >
            <i class="fa-solid fa-sliders"></i>
            More filters
          </button>
        </div>
        <div *ngIf="advanced" class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label class="pb-label">
            Recorded location
            <select class="pb-input" [(ngModel)]="filters.placement" (ngModelChange)="refresh()">
              <option value="">Any</option>
              <option value="assigned">Current spot assigned</option>
              <option value="none">No current spot recorded</option>
            </select>
          </label>
          <label class="pb-label">
            Available
            <select class="pb-input" [(ngModel)]="filters.available" (ngModelChange)="refresh()">
              <option value="">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <label class="pb-label">
            Delivered
            <select class="pb-input" [(ngModel)]="filters.delivered" (ngModelChange)="refresh()">
              <option value="">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <div></div>
          <label class="pb-label">
            Created from
            <input
              type="date"
              class="pb-input"
              [(ngModel)]="filters.created_from"
              (change)="refresh()"
            />
          </label>
          <label class="pb-label">
            Created through
            <input
              type="date"
              class="pb-input"
              [(ngModel)]="filters.created_to"
              (change)="refresh()"
            />
          </label>
          <label class="pb-label">
            Intake from
            <input
              type="date"
              class="pb-input"
              [(ngModel)]="filters.intake_from"
              (change)="refresh()"
            />
          </label>
          <label class="pb-label">
            Intake through
            <input
              type="date"
              class="pb-input"
              [(ngModel)]="filters.intake_to"
              (change)="refresh()"
            />
          </label>
        </div>
        <div class="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs pb-muted">
          <label class="inline-flex cursor-pointer items-center gap-2">
            <input type="checkbox" [(ngModel)]="grouped" (ngModelChange)="toggleGrouping()" />
            Group by customer
          </label>
          <span>
            Locations reflect current package-to-spot assignments. Click a row for details.
          </span>
          <span *ngIf="recordCount !== null && recordCount > options.export_limit">
            CSV: filter to {{ options.export_limit | number }} records or fewer.
          </span>
        </div>
      </section>
      <div *ngIf="optionsError" class="pb-alert" role="alert">
        {{ optionsError }}
        <button class="underline" (click)="loadOptions()">Retry choices</button>
      </div>
      <div *ngIf="error" class="pb-alert" role="alert">
        {{ error }}
        <button class="underline" (click)="refresh()">Retry</button>
      </div>
      <section
        class="pb-panel pb-grid-shell overflow-hidden rounded-2xl"
        [attr.aria-busy]="loading"
        aria-label="Products grid"
      >
        <div class="flex h-9 items-center justify-between px-4 text-xs pb-muted">
          <span>{{ loading ? 'Loading products…' : 'Read-only product browser' }}</span>
          <span *ngIf="updatedAt">Updated {{ updatedAt | date: 'shortTime' }}</span>
        </div>
        <ag-grid-angular
          class="ag-theme-alpine pb-grid"
          [class.ag-theme-alpine-dark]="theme === 'dark'"
          [gridOptions]="gridOptions"
          [columnDefs]="columnDefs"
          [defaultColDef]="defaultColDef"
          (gridReady)="onGridReady($event)"
          (rowClicked)="rowClicked($event)"
        ></ag-grid-angular>
      </section>
    </div>
  </div>
</app-menu>
